import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { useParams, useLocation } from "react-router-dom";
import { useTheme } from "styled-components";
import {
  fetchSubmissionDetails,
  searchSubmissionById,
  handleVote,
  countVotes,
  checkUserVote,
  fetchComments,
  addComment,
} from "../../services/submissionService";
import { initiatePurchase } from "../../services/purchaseService";
import {
  Container,
  Box,
  Typography,
  CircularProgress,
  Grid,
  Button,
  Snackbar,
  Alert,
  Backdrop,
} from "@mui/material";
import CommentInput from "../molecules/SubmissionDetails/CommentInput";
import CommentsList from "../molecules/SubmissionDetails/CommentsList";
import LikeDislikeButtons from "../molecules/SubmissionDetails/LikeDislikeButtons";
import SubmissionTitleSection from "../molecules/SubmissionDetails/SubmissionTitleSection";
import SubmissionDetailsSection from "../molecules/SubmissionDetails/SubmissionDetailsSection";
import VideoPlayerSection from "../molecules/SubmissionDetails/VideoPlayerSection";
import ImageDisplaySection from "../molecules/SubmissionDetails/ImageDisplaySection";

const SubmissionDetailPage = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const { submissionId } = useParams();
  const [submissionDetails, setSubmissionDetails] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [likeCount, setLikeCount] = useState(0);
  const [dislikeCount, setDislikeCount] = useState(0);
  const [likeStatus, setLikeStatus] = useState(null);
  const [purchaseAmount, setPurchaseAmount] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [handlingFee, sethandlingFee] = useState(0);
  const [sharesAvailable, setSharesAvailable] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [totalShares, setTotalShares] = useState(0);
  const [pricePerShare, setPricePerShare] = useState(0);
  const [remainingShares, setRemainingShares] = useState(0);
  const [purchaseQuantity, setPurchaseQuantity] = useState("");
  const [sharesToBuy, setSharesToBuy] = useState(0);
  const theme = useTheme();

  const [isProcessing, setIsProcessing] = useState(true);

  const handleAssetStatusChange = (status) => {
    setIsProcessing(status);
  };

  useEffect(() => {
    setUser(currentUser); // Set the user to currentUser from AuthContext
  }, [currentUser]); // Update the user state whenever currentUser changes

  const queryParams = new URLSearchParams(location.search);
  const transactionError = queryParams.has("transaction_error");

  useEffect(() => {
    if (transactionError) {
      setSnackbarMessage("Transaction failed. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [transactionError]);

  const getHandlingFee = (price, transactingInUS = false) => {
    if (price === 0) return 0;
    const fee = transactingInUS ? 0.971 : 0.956;
    const total = (price + 0.3) / fee;
    const handlingFee = total - price;
    sethandlingFee(parseFloat(handlingFee.toFixed(2)));
    return handlingFee;
  };

  // Fetch submission and challenge details using useCallback to stabilize the function
  const getSubmissionAndChallengeDetails = useCallback(async () => {
    try {
      const submissionData = await fetchSubmissionDetails(submissionId);
      setSubmissionDetails(submissionData);

      const submissionSearchResponse = await searchSubmissionById(
        submissionData.submissionSaleId
      );

      const pricePerShare =
        submissionSearchResponse[0]?.art?.activeSales[0]?.shares?.pricePerShare;
      const totalShares =
        submissionSearchResponse[0]?.art?.activeSales[0]?.shares?.totalShares;
      const purchasedShares =
        submissionSearchResponse[0]?.art?.activeSales[0]?.shares
          ?.purchasedShares;
      const remainingShares = totalShares - purchasedShares;

      setTotalShares(totalShares);
      setPricePerShare(pricePerShare);
      setRemainingShares(remainingShares);

      if (remainingShares > 0) {
        setSharesAvailable(true);
      }

      // Fetch like/dislike counts without requiring authentication
      const { likeCount, dislikeCount } = await countVotes(submissionId);
      setLikeCount(likeCount);
      setDislikeCount(dislikeCount);

      // If the user is authenticated, fetch their vote
      if (user) {
        try {
          const userVote = await checkUserVote(submissionId, user.uid);
          setLikeStatus(userVote);
        } catch (err) {
          console.error("Error checking user vote:", err);
        }
      }

      // Calculate total price with handling fee
      setPurchaseAmount(pricePerShare.toFixed(2));

      const total = pricePerShare + getHandlingFee(pricePerShare);
      setTotalPrice(parseFloat(total.toFixed(2)));
    } catch (err) {
      if (err.message.includes("User is not authenticated")) {
        // Set error message only for debugging purposes, do not show to the user
        console.warn(
          "User is not authenticated. Proceeding as unauthenticated."
        );
      } else {
        // setError(`${err.message || err} to see this submission.`);
        setError(
          `This submission is being processed. Please check back soon for updates.`
        );
      }
    } finally {
      setLoading(false);
    }
  }, [submissionId, user]);

  useEffect(() => {
    getSubmissionAndChallengeDetails();
  }, [getSubmissionAndChallengeDetails]);

  useEffect(() => {
    const unsubscribe = fetchComments(
      submissionId,
      setComments,
      setLoadingComments
    );
    return () => unsubscribe();
  }, [submissionId]);

  // Determine if the purchase button should be shown
  const currentUserUid = currentUser?.uid || null; // Safely get current user's UID
  console.log("currentUserUid: ", currentUserUid);

  const submissionOwnerUid = submissionDetails?.userId || null; // Safely get submission owner's UID

  console.log("submissionOwnerUid: ", submissionDetails?.userId);

  // Check if the authenticated user is the submission owner
  const isSubmissionOwner = () => currentUserUid === submissionOwnerUid;

  console.log("isSubmissionOwner: ", isSubmissionOwner());

  // Check if shares are available
  const areSharesAvailable = Boolean(sharesAvailable);

  // Check if the funding goal has been reached

  const isGoalReached =
    submissionDetails?.fundingGoal > 0 && remainingShares <= 0;

  const isSubmissionPending = pricePerShare === undefined;

  console.log("isSubmissionPending: ", isSubmissionPending);

  // Determine if the purchase button should be shown
  const showPurchaseButton =
    (!isSubmissionOwner() || currentUserUid === null) &&
    areSharesAvailable &&
    !isGoalReached;

  // Handle comment submission
  const handleCommentSubmit = async () => {
    if (!user) {
      setSnackbarMessage("You need to be logged in to comment.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (!newComment.trim()) return;

    try {
      await addComment(submissionId, newComment, user);
      setNewComment("");
    } catch (error) {
      setSnackbarMessage("Failed to add comment.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Handle like action
  const handleLike = async () => {
    if (!user) {
      setSnackbarMessage("You need to be signed in to like this submission.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // Determine the new like status
    const newStatus = likeStatus === "like" ? null : "like";

    const response = await handleVote(submissionId, user.uid, newStatus);
    if (response.success) {
      // Update the like and dislike counts
      if (newStatus === "like") {
        setLikeCount(likeCount + 1);
        if (likeStatus === "dislike") setDislikeCount(dislikeCount - 1);
      } else if (likeStatus === "like") {
        setLikeCount(likeCount - 1);
      }
      setLikeStatus(newStatus);
    } else {
      setSnackbarMessage("Failed to like this submission.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Handle dislike action
  const handleDislike = async () => {
    if (!user) {
      setSnackbarMessage(
        "You need to be signed in to dislike this submission."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // Determine the new dislike status
    const newStatus = likeStatus === "dislike" ? null : "dislike";

    const response = await handleVote(submissionId, user.uid, newStatus);
    if (response.success) {
      // Update the like and dislike counts
      if (newStatus === "dislike") {
        setDislikeCount(dislikeCount + 1);
        if (likeStatus === "like") setLikeCount(likeCount - 1);
      } else if (likeStatus === "dislike") {
        setDislikeCount(dislikeCount - 1);
      }
      setLikeStatus(newStatus);
    } else {
      setSnackbarMessage("Failed to dislike this submission.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const changeHandler = (e) => {
    const value = e.target.value;

    // Handle empty input
    if (value === "") {
      setPurchaseQuantity(""); // Keep the input empty if the user clears the field
      setSharesToBuy(0); // Reset shares
      setTotalPrice(0); // Reset total price
      return;
    }

    // Prevent multiple leading zeros
    if (value.startsWith("0") && value.length > 1) {
      setPurchaseQuantity(value.replace(/^0+/, "")); // Remove leading zeros
      return;
    }

    // Convert to a number after ensuring it's not empty
    const numericValue = Number(value);

    // Ensure the user input is valid
    if (isNaN(numericValue) || numericValue <= 0) {
      setPurchaseQuantity(""); // Reset to an empty string if the input is invalid
      setSharesToBuy(0); // Reset shares to 0 if the input is invalid
      setTotalPrice(0); // Reset total price if invalid
      return;
    }

    // Calculate the maximum allowable purchase amount
    const maxPurchaseAmount = pricePerShare * remainingShares;

    // Ensure the purchase amount does not exceed the max amount
    let purchaseAmount = numericValue;
    if (numericValue > maxPurchaseAmount) {
      purchaseAmount = maxPurchaseAmount; // Cap the value at the maximum allowable amount
    }

    setPurchaseQuantity(purchaseAmount.toString()); // Allow the current input

    // Calculate the number of shares based on the capped value
    const shares = purchaseAmount / pricePerShare;
    setSharesToBuy(shares); // Update shares to buy
    setPurchaseAmount(purchaseAmount.toFixed(2));

    // Calculate the total price with handling fee
    const total = purchaseAmount + getHandlingFee(purchaseAmount);
    setTotalPrice(parseFloat(total.toFixed(2)));
  };

  const handleBlur = () => {
    let value = Number(purchaseQuantity);

    // Calculate the maximum allowable purchase amount
    const maxPurchaseAmount = pricePerShare * remainingShares;

    // If the value is less than the minimum (5), set it to 5
    if (value < 5) {
      value = 5;
    }

    // Round to the nearest multiple of 5
    if (value % 5 !== 0) {
      value = Math.round(value / 5) * 5;
    }

    // Ensure the value does not exceed the maximum allowable amount
    if (value > maxPurchaseAmount) {
      value = maxPurchaseAmount; // Cap the value at the maximum allowable amount
    }

    setPurchaseQuantity(value.toString()); // Update the input to the rounded and capped value

    // Recalculate shares and total based on the rounded and capped value
    const shares = value / pricePerShare;
    setSharesToBuy(shares);

    setPurchaseAmount(value.toFixed(2));

    const total = value + getHandlingFee(value);
    setTotalPrice(parseFloat(total.toFixed(2)));
  };

  // Handle purchase action
  const handlePurchase = async () => {
    if (!user) {
      setSnackbarMessage(
        "You need to be signed in to purchase this submission."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // Check if the purchase quantity is valid
    if (!purchaseQuantity || purchaseQuantity <= 0) {
      setSnackbarMessage("Please enter a valid amount to purchase.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      // Retrieve the auth token from localStorage
      const authResponseData = JSON.parse(
        localStorage.getItem("authResponseData")
      );
      const token = authResponseData?.token;

      // Ensure the user is authenticated
      if (!token) {
        setSnackbarMessage("You must be signed in to initiate a purchase.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      console.log("submissionDetails paypal: ", submissionDetails);

      const baseKbeanURL = process.env.REACT_APP_PAYPAL_BASE_KBEAN_URL;

      console.log("baseKbeanURL: ", baseKbeanURL);

      // Prepare purchase data
      const purchaseData = {
        ArtworkId: submissionDetails.submissionSaleId,
        ArtSaleId: submissionDetails.saleId,
        Shares: sharesToBuy,
        PricePerShare: pricePerShare,
        TotalAmount: totalPrice,
        HandlingFee: handlingFee,
        SuperCoinsUsed: "",
        ClientSuccessUrl: `${baseKbeanURL}/challenge/submission/contributions`,
        ClientCancelUrl: `${baseKbeanURL}/challenge/submission/${submissionId}?transaction_error`,
      };

      console.log("purchaseData paypal: ", purchaseData);

      // Initiate the purchase
      const response = await initiatePurchase(purchaseData);
      if (response) {
        console.log("Purchase initiation response:", response);
        window.location.href = response; // Redirect user to the provided response URL
      }
    } catch (error) {
      // Handle any errors that occur during the purchase initiation
      console.error("Error initiating purchase:", error);
      setSnackbarMessage("Failed to initiate purchase. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Handle Snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1, // Ensures it's above other elements
          backgroundColor: "rgba(0, 0, 0, 0.5)", // Slightly opaque black
        }}
        open={true} // Always open when in this loading state
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h6" color={theme.colors.red}>
          {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Link and Title Section */}
      <SubmissionTitleSection
        submissionDetails={submissionDetails}
        theme={theme}
      />

      {/* Submission Details Section */}
      <SubmissionDetailsSection
        submissionDetails={submissionDetails}
        theme={theme}
      />

      {submissionDetails.contentType.startsWith("video/") ? (
        // Video Player Section
        <VideoPlayerSection
          submission={submissionDetails}
          onAssetStatusChange={handleAssetStatusChange}
        />
      ) : submissionDetails.contentType.startsWith("image/") ? (
        // Image Display Section
        <ImageDisplaySection
          submission={submissionDetails}
          onAssetStatusChange={handleAssetStatusChange}
        />
      ) : (
        // Fallback for unsupported content types
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            marginTop: "10px",
            fontSize: "0.9em",
            fontFamily: "basic-sans",
          }}
        >
          Unsupported content type: {submissionDetails.contentType}
        </Typography>
      )}

      {/* Like/Dislike/Purchase Section */}
      {!isProcessing && (
        <Box sx={{ mb: 4 }}>
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            {/* Like/Dislike Section */}
            <Box>
              <Grid container spacing={2} alignItems="center">
                <LikeDislikeButtons
                  likeCount={likeCount}
                  dislikeCount={dislikeCount}
                  likeStatus={likeStatus}
                  handleLike={handleLike}
                  handleDislike={handleDislike}
                  theme={theme}
                />
              </Grid>
            </Box>

            {/* Goal Reached Message, Purchase Option, or Submission Pending */}
            <Grid item>
              {isSubmissionPending ? (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    marginTop: "10px",
                    fontSize: "0.9em",
                    fontFamily: "basic-sans",
                  }}
                >
                  Submission Pending
                </Typography>
              ) : isGoalReached ? (
                <Box sx={{ width: "100%", marginTop: "10px" }}>
                  <Box
                    sx={{
                      backgroundColor: theme.colors.palePurple,
                      borderRadius: "50px",
                      overflow: "hidden",
                      position: "relative",
                      height: "20px",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        backgroundColor: theme.colors.green,
                        height: "100%",
                        transition: "width 0.3s ease-in-out",
                      }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textAlign: "center",
                      marginTop: "5px",
                      fontSize: "0.9em",
                      fontFamily: "basic-sans",
                    }}
                  >
                    Goal Reached! The funding target of $
                    {submissionDetails.fundingGoal} has been fully met.
                  </Typography>
                </Box>
              ) : (
                showPurchaseButton && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                      width: "100%", // Make the box fill the container
                    }}
                  >
                    {/* Dollar sign next to the number input */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        position: "relative",
                        flexGrow: 1, // Make the input field grow to fill available space
                      }}
                    >
                      <span
                        style={{
                          position: "absolute",
                          left: "-20px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#777",
                          fontSize: "1em",
                        }}
                      >
                        $
                      </span>
                      <input
                        type="text"
                        min="5"
                        step="5"
                        value={purchaseQuantity}
                        onChange={changeHandler}
                        onBlur={handleBlur}
                        inputMode="numeric" // Use numeric input mode for better mobile support
                        pattern="[0-9]*" // Only allow numbers
                        style={{
                          paddingLeft: "25px", // Add padding for the dollar sign
                          width: "100%", // Make the input field fill available space
                          padding: "5px",
                          borderRadius: "5px",
                          textAlign: "left",
                          outline: "none",
                        }}
                      />
                    </Box>

                    {/* Purchase Button */}
                    <Button
                      variant="contained"
                      sx={{
                        flexGrow: 1, // Make the button grow to fill available space
                        backgroundColor: theme.colors.green,
                        color: "white",
                        borderRadius: "5px",
                        textTransform: "none",
                        padding: "5px 20px",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: theme.colors.blue,
                          boxShadow: "none",
                        },
                      }}
                      onClick={handlePurchase}
                    >
                      {submissionDetails.challengeData &&
                      submissionDetails.challengeData.allowCrowdFunding
                        ? `Contribute: $${purchaseAmount} + $${handlingFee} Fee`
                        : `Buy to Own: $${purchaseAmount} + $${handlingFee} Fee`}
                    </Button>

                    {/* Progress Bar for Funding Goal */}
                    <Box sx={{ width: "100%", marginTop: "10px" }}>
                      <Box
                        sx={{
                          backgroundColor: theme.colors.palePurple,
                          borderRadius: "50px",
                          overflow: "hidden",
                          position: "relative",
                          height: "20px",
                          width: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${
                              ((pricePerShare *
                                (totalShares - remainingShares)) /
                                (pricePerShare * totalShares)) *
                              100
                            }%`,
                            backgroundColor: theme.colors.green,
                            height: "100%",
                            transition: "width 0.3s ease-in-out",
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          textAlign: "center",
                          marginTop: "5px",
                          fontSize: "0.9em",
                          fontFamily: "basic-sans",
                        }}
                      >
                        {submissionDetails.challengeData &&
                        submissionDetails.challengeData.allowCrowdFunding
                          ? `$${new Intl.NumberFormat().format(
                              pricePerShare * (totalShares - remainingShares)
                            )} contributed so far, $${new Intl.NumberFormat().format(
                              pricePerShare * remainingShares
                            )} left to go!`
                          : `${new Intl.NumberFormat().format(
                              totalShares - remainingShares
                            )} ${
                              submissionDetails.contentType.startsWith("video/")
                                ? "videos"
                                : submissionDetails.contentType.startsWith(
                                    "image/"
                                  )
                                ? "images"
                                : "items"
                            } purchased so far, ${new Intl.NumberFormat().format(
                              remainingShares
                            )} still available!`}
                      </Typography>
                    </Box>
                  </Box>
                )
              )}
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Comments Section */}
      {!isProcessing && (
        <Box sx={{ mt: 6 }}>
          <Typography
            variant="body1"
            sx={{ mb: 2, fontFamily: "basic-sans", fontWeight: "700", ml: 3 }}
          >
            Comments on this Submission
          </Typography>

          <CommentInput
            newComment={newComment}
            setNewComment={setNewComment}
            handleCommentSubmit={handleCommentSubmit}
          />

          <CommentsList
            comments={comments}
            loadingComments={loadingComments}
            theme={theme}
          />
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SubmissionDetailPage;
